import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Form, Spin } from 'antd'
import { flatten, get, groupBy, map, uniqBy } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  IngredientsAutocheckTree,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  getIncludedAllergensOfId,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  idPropType,
  nodePropType,
  simpleEntityPropType,
} from '../../../common/prop-types'
import { getFormItemLabelAboveConfiguration } from '../../../common/utils'

import { useUpdateDietExcludedIngredientsDetailsMutation } from '../../hooks'
import { setDietPageIsDirty } from '../../redux/actions/diet-actions'

import { dietExcludedIngredientsDetailsFormInitialValues } from './initial-values'

const DietExcludedIngredientsDetailsForm = ({
  ingredientTreeData,
  allergens,
  excludedIngredients,
  excludedAllergens,
  dietId,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DIET.SAVE_INGREDIENTS,
  ])

  const [
    updateDietExcludedIngredientsDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDietExcludedIngredientsDetailsMutation()

  const initialValues = useMemo(
    () => dietExcludedIngredientsDetailsFormInitialValues(excludedIngredients),
    [excludedIngredients],
  )

  const excludedIngredientsFromAllergens = useMemo(() => {
    const allergensGroupedById = groupBy(allergens, 'id')

    const includedAllergensFromAllExcludedItems = flatten(
      map(excludedAllergens, ({ id }) =>
        getIncludedAllergensOfId(id, allergensGroupedById),
      ),
    )

    return uniqBy(
      flatten(
        map(
          [...excludedAllergens, ...includedAllergensFromAllExcludedItems],
          ({ id }) =>
            map(
              get(allergensGroupedById, `${id}[0].includedIngredients`),
              ingredient => ({
                ...ingredient,
                source: `Allergen ${get(
                  allergensGroupedById,
                  `${id}[0].name`,
                )}`,
                sourceBadgeType: 'warning',
              }),
            ),
        ),
      ),
      'id',
    )
  }, [allergens, excludedAllergens])

  const handleSaveData = useCallback(
    data => updateDietExcludedIngredientsDetails(dietId, data),
    [dietId, updateDietExcludedIngredientsDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Diet details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [excludedIngredients, resetForm])

  useEffect(() => {
    dispatch(setDietPageIsDirty({ isExcludedIngredientsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editDiet"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledVerticalSpace>
        <FormButtons
          permissions={[PERMISSIONS.DIET.SAVE_INGREDIENTS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
        <Form.Item
          name="dietExcludedIngredientIds"
          label="Excluded Ingredients"
          labelCol={getFormItemLabelAboveConfiguration(true)}
        >
          <IngredientsAutocheckTree
            disabled={!isFormEnabled}
            ingredientsFromOtherSources={excludedIngredientsFromAllergens}
            treeData={ingredientTreeData}
            includeComponentIngredients
          />
        </Form.Item>
      </StyledVerticalSpace>
    </DirtyPromptForm>
  )
}

DietExcludedIngredientsDetailsForm.propTypes = {
  ingredientTreeData: PropTypes.arrayOf(nodePropType).isRequired,
  excludedIngredients: PropTypes.arrayOf(PropTypes.any),
  excludedAllergens: PropTypes.arrayOf(simpleEntityPropType),
  allergens: PropTypes.arrayOf(PropTypes.any.isRequired).isRequired,
  dietId: idPropType.isRequired,
}

DietExcludedIngredientsDetailsForm.defaultProps = {
  excludedIngredients: [],
  excludedAllergens: [],
}

export default DietExcludedIngredientsDetailsForm
